import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Container from "../../components/Container";
import { getRestaurants, SUBSCRIPTION_PLANS } from "./contentManagerData";

const COLORS = {
  primary: "#2DD736",
  dark: "#053418",
  cta: "#0A5529",
  grey: "#375D46",
  greenGray: "#E5EAD9",
  lighter: "#D5F4D7",
  white: "#FFFFFF",
};

const PLAN_MAP = {
  Basic: { bg: "#F3F4F6", color: "#374151" },
  Pro: { bg: "#FEF3C7", color: "#92400E" },
  Enterprise: { bg: "#D5F4D7", color: "#0A5529" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Paper
    elevation={0}
    sx={{ p: 2.5, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9" }}
  >
    <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: COLORS.grey, mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "GilroyBold", fontSize: 28, color: color || COLORS.dark, lineHeight: 1 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 11.5, color: "#9CA3AF", mt: 0.5 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const ContentManager = (props) => {
  const navigate = useNavigate();
  const restaurants = useMemo(() => getRestaurants(), []);

  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [sortBy, setSortBy] = useState("content_desc");

  const filtered = useMemo(() => {
    let list = restaurants.filter((r) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        r.restaurant_name.toLowerCase().includes(q) ||
        r.location.toLowerCase().includes(q) ||
        (r.owner || "").toLowerCase().includes(q);
      const matchPlan = planFilter === "all" || r.subscription_plan === planFilter;
      return matchSearch && matchPlan;
    });
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case "content_asc":
          return a.content_count - b.content_count;
        case "name_asc":
          return a.restaurant_name.localeCompare(b.restaurant_name);
        case "recent":
          return (b.last_generated_at || "").localeCompare(a.last_generated_at || "");
        case "content_desc":
        default:
          return b.content_count - a.content_count;
      }
    });
    return list;
  }, [restaurants, search, planFilter, sortBy]);

  const totalContent = restaurants.reduce((s, r) => s + r.content_count, 0);
  const totalImages = restaurants.reduce((s, r) => s + r.image_count, 0);
  const totalVideos = restaurants.reduce((s, r) => s + r.video_count, 0);

  return (
    <Container {...props} page="content-manager" onSearch={() => {}}>
      <Box sx={{ maxWidth: 1400 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <PhotoLibraryOutlinedIcon sx={{ fontSize: 22, color: COLORS.cta }} />
            <Typography sx={{ fontFamily: "GilroyBold", fontSize: 26, color: COLORS.dark }}>
              Content Manager
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 13.5, color: COLORS.grey }}>
            AI-generated content across all restaurants. Open a restaurant to view its generation history.
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Restaurants" value={restaurants.length} sub="With generated content" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Total Content" value={totalContent} sub="All restaurants" color={COLORS.cta} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Images" value={totalImages} sub="Generated images" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Videos / Reels" value={totalVideos} sub="Generated videos" />
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper
          elevation={0}
          sx={{ p: 2, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9", mb: 2 }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search restaurant, location or owner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  sx: { fontFamily: "GilroyRegular", fontSize: 13, borderRadius: "8px" },
                }}
                sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Select
                fullWidth
                size="small"
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              >
                <MenuItem value="all">All Plans</MenuItem>
                {SUBSCRIPTION_PLANS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Select
                fullWidth
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              >
                <MenuItem value="content_desc">Most content</MenuItem>
                <MenuItem value="content_asc">Least content</MenuItem>
                <MenuItem value="recent">Recently generated</MenuItem>
                <MenuItem value="name_asc">Name (A–Z)</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.grey }}>
                {filtered.length} restaurant{filtered.length !== 1 ? "s" : ""}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper
          elevation={0}
          sx={{ borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9", overflow: "hidden" }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                  {["Restaurant", "Location", "Plan", "Content", "Images", "Videos", "Last Generated", ""].map((h, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        fontFamily: "GilroySemiBold",
                        fontSize: 12,
                        color: COLORS.grey,
                        borderBottom: "1px solid #E5EAD9",
                        py: 1.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: COLORS.grey, fontFamily: "GilroyRegular" }}>
                      No restaurants match your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => {
                    const p = PLAN_MAP[row.subscription_plan] || PLAN_MAP.Basic;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/content-manager/${row.id}`)}
                        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#FAFDF9" }, "&:last-child td": { border: 0 } }}
                      >
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Avatar src={row.logo_url} alt={row.restaurant_name} sx={{ width: 34, height: 34 }} />
                            <Box>
                              <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.dark }}>
                                {row.restaurant_name}
                              </Typography>
                              {row.owner && (
                                <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 11.5, color: "#9CA3AF" }}>
                                  {row.owner}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "GilroyRegular", fontSize: 13, color: "#6B7280", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>
                          {row.location}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Chip label={row.subscription_plan} size="small" sx={{ fontFamily: "GilroySemiBold", fontSize: 11, backgroundColor: p.bg, color: p.color, height: 22 }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Chip
                            label={row.content_count}
                            size="small"
                            sx={{ fontFamily: "GilroyBold", fontSize: 12, backgroundColor: COLORS.lighter, color: COLORS.cta, height: 24, minWidth: 40 }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6B7280" }}>
                            <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 13 }}>{row.image_count}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6B7280" }}>
                            <MovieOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 13 }}>{row.video_count}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: "#6B7280", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>
                          {fmtDate(row.last_generated_at)}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: "1px solid #F3F4F6" }}>
                          <ChevronRightIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default ContentManager;
